// Collaterals.jsx — registry with PIN-code + lat/lon capture
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box, Paper, Button, Table, TableHead, TableRow, TableCell, TableBody,
  TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, IconButton, Tooltip, CircularProgress, Autocomplete, Alert,
} from "@mui/material";
import {
  Plus, Search, ArrowRight, Trash2, Map as MapIcon, RefreshCw,
  PlayCircle, Filter, MapPin,
} from "lucide-react";
import { toast } from "react-toastify";
import DashboardLayout from "../layouts/DashboardLayout";
import RiskPill from "../components/RiskPill";
import { borrowersApi, collateralsApi, riskApi, formatApiError } from "../api/api";
import { fmtINR, fmtScore, HAZARD_LABELS } from "../utils/format";

const TYPES = [
  "plant", "office", "warehouse", "land",
  "residential_property", "commercial_property",
  "equipment", "vehicle", "inventory",
];

const CONSTRUCTION = ["rcc", "steel", "brick", "semi-pucca", "kuccha", "prefab", "tin"];
const CRITICALITY = ["Low", "Medium", "High", "Critical"];

export default function Collaterals() {
  const navigate = useNavigate();
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const initialBorrowerId = params.get("borrower_id") || "";

  const [collList, setCollList] = useState([]);
  const [borrowerList, setBorrowerList] = useState([]);
  const [filterBorrower, setFilterBorrower] = useState(initialBorrowerId);
  const [filterType, setFilterType] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(defaultForm(initialBorrowerId));
  const [geocoding, setGeocoding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scoreMap, setScoreMap] = useState({});

  function defaultForm(borrower_id = "") {
    return {
      borrower_id, name: "", type: "plant",
      pin_code: "", state: "", city: "", address: "",
      latitude: "", longitude: "",
      current_valuation_inr: "", asset_age_years: 0,
      elevation_m: "", coastal_distance_km: "",
      construction_type: "rcc", criticality: "Medium",
      ltv_pct: 60, insurance_cover_inr: 0, hypothecation_pct: 100,
    };
  }

  const loadBorrowers = useCallback(async () => {
    const { data } = await borrowersApi.list();
    setBorrowerList(data.borrowers || []);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = {};
      if (filterBorrower) q.borrower_id = filterBorrower;
      if (filterType) q.type = filterType;
      const { data } = await collateralsApi.list(q);
      setCollList(data.collaterals || []);

      const { data: rdata } = await riskApi.cachedPortfolio("current");
      const sm = {};
      (rdata.results || []).forEach((r) => { sm[r.collateral_id] = r; });
      setScoreMap(sm);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filterBorrower, filterType]);

  useEffect(() => { loadBorrowers(); load(); /* eslint-disable-next-line */ }, [filterBorrower, filterType]);

  const filtered = collList.filter((c) =>
    !search || (c.name + " " + (c.pin_code || "") + " " + (c.state || "")).toLowerCase().includes(search.toLowerCase())
  );

  // PIN -> lat/lon
  const resolvePin = async () => {
    const p = (form.pin_code || "").trim();
    if (!/^\d{6}$/.test(p)) { toast.warn("Enter a 6-digit PIN code"); return; }
    setGeocoding(true);
    try {
      const { data } = await collateralsApi.geocode(p);
      setForm((f) => ({
        ...f,
        latitude: data.latitude.toFixed(6),
        longitude: data.longitude.toFixed(6),
        state: f.state || data.state || "",
        city: f.city || data.city || "",
      }));
      toast.success(`PIN ${p} resolved (${data.source})${data.approximate ? " — approximate" : ""}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setGeocoding(false); }
  };

  const save = async () => {
    if (!form.borrower_id) { toast.warn("Select a borrower"); return; }
    if (!form.name || !form.latitude || !form.longitude) {
      toast.warn("Name + latitude + longitude are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      [
        "latitude", "longitude", "current_valuation_inr", "asset_age_years",
        "elevation_m", "coastal_distance_km", "ltv_pct", "insurance_cover_inr",
        "hypothecation_pct",
      ].forEach((k) => {
        payload[k] = payload[k] === "" || payload[k] == null ? null : Number(payload[k]);
      });
      const { data } = await collateralsApi.create(payload);
      // auto-score
      try { await riskApi.collateral(data.collateral.collateral_id, "current"); } catch {}
      toast.success(`Collateral "${form.name}" registered and scored.`);
      setOpen(false); setForm(defaultForm(filterBorrower || "")); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try { await collateralsApi.remove(c.collateral_id); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const rescore = async (c) => {
    try {
      await riskApi.collateral(c.collateral_id, "current");
      toast.success("Re-scored");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const borrowerName = (bid) => borrowerList.find((b) => b.borrower_id === bid)?.name || "—";

  return (
    <DashboardLayout
      title="Collateral Registry"
      subtitle="GEOSPATIAL · VALUATION · RISK SCORING"
      headerExtra={
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Button size="small" variant="outlined" onClick={() => navigate("/hazard-map")}
            startIcon={<MapIcon size={13} />}>
            Map view
          </Button>
          <Button size="small" variant="contained" onClick={() => { setForm(defaultForm(filterBorrower || "")); setOpen(true); }}
            startIcon={<Plus size={14} />}
            data-testid="add-collateral-btn"
            sx={{ background: "#1E2A38", "&:hover": { background: "#0F1721" } }}>
            Register collateral
          </Button>
        </Box>
      }>
      {/* Filters */}
      <Paper sx={{ p: 1.4, mb: 2, display: "flex", gap: 1.2, alignItems: "center", flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, color: "#6B7280", fontSize: 11.5, fontFamily: "IBM Plex Mono, monospace", letterSpacing: "0.1em", textTransform: "uppercase" }}>
          <Filter size={13} /> Filters
        </Box>
        <TextField size="small" placeholder="Search name / PIN / state…" value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <Search size={14} style={{ marginRight: 6, color: "#94A3B8" }} /> }}
          inputProps={{ "data-testid": "coll-search" }}
          sx={{ minWidth: 240 }} />
        <TextField size="small" select label="Borrower" value={filterBorrower}
          onChange={(e) => setFilterBorrower(e.target.value)}
          SelectProps={{ inputProps: { "data-testid": "coll-filter-borrower" } }}
          sx={{ minWidth: 200 }}>
          <MenuItem value="">All borrowers</MenuItem>
          {borrowerList.map((b) => <MenuItem key={b.borrower_id} value={b.borrower_id}>{b.name}</MenuItem>)}
        </TextField>
        <TextField size="small" select label="Type" value={filterType}
          onChange={(e) => setFilterType(e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="">All types</MenuItem>
          {TYPES.map((t) => <MenuItem key={t} value={t}>{t.replace("_", " ")}</MenuItem>)}
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Chip label={`${filtered.length} collaterals`} size="small" />
      </Paper>

      <Paper sx={{ overflow: "hidden" }}>
        {loading ? <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress size={24} /></Box> : (
          <Table size="small" data-testid="collaterals-table">
            <TableHead>
              <TableRow>
                <TableCell>Collateral / Borrower</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>PIN · City · State</TableCell>
                <TableCell>Lat/Lon</TableCell>
                <TableCell align="right">Valuation</TableCell>
                <TableCell>Top hazard</TableCell>
                <TableCell align="right">Score</TableCell>
                <TableCell align="right">Risk</TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 5, color: "#94A3B8" }}>
                  No collaterals match your filter.
                </TableCell></TableRow>
              )}
              {filtered.map((c) => {
                const s = scoreMap[c.collateral_id];
                return (
                  <TableRow key={c.collateral_id} hover data-testid={`coll-row-${c.collateral_id}`}>
                    <TableCell>
                      <Box sx={{ fontWeight: 600, color: "#111827" }}>{c.name}</Box>
                      <Box sx={{ fontSize: 11.5, color: "#6B7280" }}>{borrowerName(c.borrower_id)}</Box>
                    </TableCell>
                    <TableCell sx={{ textTransform: "capitalize" }}>{c.type?.replace("_", " ")}</TableCell>
                    <TableCell>
                      <Box className="cb-mono" sx={{ fontSize: 11.5 }}>{c.pin_code || "—"}</Box>
                      <Box sx={{ fontSize: 11.5, color: "#6B7280" }}>{c.city || ""} · {c.state || ""}</Box>
                    </TableCell>
                    <TableCell className="cb-mono" sx={{ fontSize: 11.5 }}>
                      {Number(c.latitude).toFixed(3)}, {Number(c.longitude).toFixed(3)}
                    </TableCell>
                    <TableCell align="right" className="cb-mono">{fmtINR(c.current_valuation_inr)}</TableCell>
                    <TableCell>
                      {s?.top_hazard ? <span className="hazard-chip">{HAZARD_LABELS[s.top_hazard] || s.top_hazard}</span> : "—"}
                    </TableCell>
                    <TableCell align="right" className="cb-mono" sx={{ fontWeight: 600 }}>{fmtScore(s?.overall_score)}</TableCell>
                    <TableCell align="right">{s ? <RiskPill level={s.risk_level} /> : <Chip size="small" label="UNSCORED" sx={{ fontSize: 9.5 }} />}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Re-score">
                        <IconButton size="small" onClick={() => rescore(c)} data-testid={`coll-rescore-${c.collateral_id}`}>
                          <RefreshCw size={13} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Open">
                        <IconButton size="small" onClick={() => navigate(`/collaterals/${c.collateral_id}`)} data-testid={`coll-open-${c.collateral_id}`}>
                          <ArrowRight size={14} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => remove(c)} data-testid={`coll-del-${c.collateral_id}`}>
                          <Trash2 size={13} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Add collateral dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontFamily: "Playfair Display, serif", fontWeight: 600 }}>Register collateral</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" icon={<MapPin size={14} />} sx={{ mb: 2 }}>
            Enter the PIN code and click <b>Resolve</b> to auto-fill latitude / longitude via Open-Street-Map.
            You can override the coordinates manually if needed.
          </Alert>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.6 }}>
            <Autocomplete
              size="small" options={borrowerList} getOptionLabel={(o) => `${o.cif_no} · ${o.name}`}
              value={borrowerList.find((b) => b.borrower_id === form.borrower_id) || null}
              onChange={(_, v) => setForm({ ...form, borrower_id: v?.borrower_id || "" })}
              renderInput={(p) => <TextField {...p} label="Borrower" required
                inputProps={{ ...p.inputProps, "data-testid": "cf-borrower" }} />}
            />
            <TextField size="small" required label="Collateral name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              inputProps={{ "data-testid": "cf-name" }} />
            <TextField size="small" select label="Type" value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              SelectProps={{ inputProps: { "data-testid": "cf-type" } }}>
              {TYPES.map((t) => <MenuItem key={t} value={t}>{t.replace("_", " ")}</MenuItem>)}
            </TextField>
            <TextField size="small" select label="Criticality" value={form.criticality}
              onChange={(e) => setForm({ ...form, criticality: e.target.value })}>
              {CRITICALITY.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>

            {/* PIN + resolver */}
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField size="small" required label="PIN code" value={form.pin_code}
                onChange={(e) => setForm({ ...form, pin_code: e.target.value })}
                inputProps={{ "data-testid": "cf-pin", maxLength: 6 }}
                sx={{ flex: 1 }} />
              <Button onClick={resolvePin} variant="outlined" disabled={geocoding}
                data-testid="cf-resolve-pin"
                sx={{ minWidth: 100 }}>
                {geocoding ? <CircularProgress size={12} /> : "Resolve"}
              </Button>
            </Box>
            <TextField size="small" label="City" value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              inputProps={{ "data-testid": "cf-city" }} />
            <TextField size="small" label="State" value={form.state}
              onChange={(e) => setForm({ ...form, state: e.target.value })}
              inputProps={{ "data-testid": "cf-state" }} />
            <TextField size="small" label="Address line" value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })} />

            <TextField size="small" required label="Latitude" type="number" value={form.latitude}
              onChange={(e) => setForm({ ...form, latitude: e.target.value })}
              inputProps={{ "data-testid": "cf-lat", step: "0.000001" }} />
            <TextField size="small" required label="Longitude" type="number" value={form.longitude}
              onChange={(e) => setForm({ ...form, longitude: e.target.value })}
              inputProps={{ "data-testid": "cf-lon", step: "0.000001" }} />

            <TextField size="small" required label="Current valuation (INR)" type="number"
              value={form.current_valuation_inr}
              onChange={(e) => setForm({ ...form, current_valuation_inr: e.target.value })}
              inputProps={{ "data-testid": "cf-valuation" }} />
            <TextField size="small" label="Insurance cover (INR)" type="number"
              value={form.insurance_cover_inr}
              onChange={(e) => setForm({ ...form, insurance_cover_inr: e.target.value })} />

            <TextField size="small" select label="Construction" value={form.construction_type}
              onChange={(e) => setForm({ ...form, construction_type: e.target.value })}>
              {CONSTRUCTION.map((c) => <MenuItem key={c} value={c}>{c.toUpperCase()}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Asset age (yrs)" type="number" value={form.asset_age_years}
              onChange={(e) => setForm({ ...form, asset_age_years: e.target.value })} />

            <TextField size="small" label="Elevation (m)" type="number" value={form.elevation_m}
              onChange={(e) => setForm({ ...form, elevation_m: e.target.value })} />
            <TextField size="small" label="Coastal distance (km)" type="number" value={form.coastal_distance_km}
              onChange={(e) => setForm({ ...form, coastal_distance_km: e.target.value })} />

            <TextField size="small" label="LTV (%)" type="number" value={form.ltv_pct}
              onChange={(e) => setForm({ ...form, ltv_pct: e.target.value })} />
            <TextField size="small" label="Hypothecation (%)" type="number" value={form.hypothecation_pct}
              onChange={(e) => setForm({ ...form, hypothecation_pct: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}
            data-testid="cf-save"
            startIcon={<PlayCircle size={14} />}
            sx={{ background: "#1E2A38", "&:hover": { background: "#0F1721" } }}>
            {saving ? "Saving…" : "Register & score"}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
